/** @jsx jsx */

import { jsx } from '@emotion/react';
import { Alert, Link } from '@zapier/design-system';
import { useState, useEffect } from 'react';

import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { BannerProps } from './types';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

const ANNUAL_UPDATE_DISMISSED_KEY = 'annualUpsellDismissedOn';

const calculateAnnualSavingsAmount = (data: ResponseData) => {
  const { currentAccount } = data;
  const currentPlan = currentAccount.plan;
  const alternatePlan = data.possiblePlans
    .find((possiblePlan) => {
      return possiblePlan.planType === currentPlan.type;
    })
    ?.plans.find((plan) => {
      return plan.name === currentPlan.name && plan.interval === 'year';
    });
  const currentMonthlyAmount = currentPlan.amount || 0;
  const alternateMonthlyAmount =
    (alternatePlan?.amount || currentMonthlyAmount) / 12;
  const difference = currentMonthlyAmount - alternateMonthlyAmount;
  const savingsAmount = difference / currentMonthlyAmount;
  return {
    savingsAmount,
    alternatePlan,
  };
};

export const shouldRender = (data: ResponseData) => {
  const {
    currentAccount: { plan },
  } = data;
  // If they're already an annual plan they can't be upsold, and users on legacy
  // plans shouldn't see the banner.
  const isEligibleForAnnualUpsell =
    plan.interval !== 'year' && plan.isInPlanGroup;
  const hasDismissedAnnualUpsell =
    localStorage.getItem(ANNUAL_UPDATE_DISMISSED_KEY) !== null;

  if (hasDismissedAnnualUpsell || !isEligibleForAnnualUpsell) {
    return false;
  }

  return calculateAnnualSavingsAmount(data).savingsAmount > 0;
};

export const Component = function AnnualBillingUpsell(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const [annualUpsellDismissedOn, setAnnualUpsellDismissedOn] = useState(
    localStorage.getItem(ANNUAL_UPDATE_DISMISSED_KEY)
  );

  useEffect(() => {
    if (annualUpsellDismissedOn !== null) {
      localStorage.setItem(
        ANNUAL_UPDATE_DISMISSED_KEY,
        annualUpsellDismissedOn
      );
    }
  }, [annualUpsellDismissedOn]);

  const { data } = props;
  const { savingsAmount, alternatePlan } = calculateAnnualSavingsAmount(data);
  const savingsPercent = `${Math.floor(savingsAmount * 100)}%`;
  const upgradeUrl = encodeURI(
    alternatePlan?.id
      ? `/app/planbuilder/checkout?planId=${alternatePlan?.id}`
      : '/app/planbuilder/plans'
  );

  const onUpgrade = () => {
    props.trackEvent('clicked annual billing nudge');
  };

  const onDismiss = () => {
    setAnnualUpsellDismissedOn(new Date().toUTCString());
  };

  const title = (
    <span>
      {`Save ${savingsPercent} when you `}
      <Link href={getFullyQualifiedURL(upgradeUrl)} onClick={onUpgrade}>
        switch to an annual subscription
      </Link>
    </span>
  );

  if (annualUpsellDismissedOn) {
    return null;
  }

  return (
    <Alert
      layout={props.layout}
      title={title}
      type="info"
      onDismiss={onDismiss}
    />
  );
};
